/**
 * BuyTogether — a "frequently bought together" bundle on the PDP.
 *
 * Shows the main product tile + up to 2 companion products from other
 * categories, joined by a plus sign, with a subtotal and a single
 * "Add all to cart" CTA that queues each product to the shopper's
 * marketplace cart in one shot.
 *
 * Data comes from `GET /api/shop/products/{slug}` — the payload now
 * includes a `buy_together` array (see backend `shop_public.py`).
 */
import React from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Loader2, ShoppingBag, Sparkles } from "lucide-react";
import { toast } from "sonner";
import type { Product } from "@/lib/types";
import { api } from "@/lib/api";
import { ecom } from "@/lib/analytics";
import { productImage } from "@/lib/productImage";
import { useCurrency } from "@/contexts/CurrencyContext";
import { SmartImage } from "@/components/shared/SmartImage";
import { useSamplePool } from "@/lib/sampleImages";

interface Props {
  main: Product;
  companions: Product[];
}

export const BuyTogether: React.FC<Props> = ({ main, companions }) => {
  const { formatPrice } = useCurrency();
  useSamplePool();
  const qc = useQueryClient();

  // Selection lets the shopper de-select any companion before adding all.
  const [selected, setSelected] = React.useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = { [main.product_id]: true };
    companions.forEach((c) => { initial[c.product_id] = true; });
    return initial;
  });

  const selectedProducts = React.useMemo(() => {
    const pool = [main, ...companions];
    return pool.filter((p) => selected[p.product_id]);
  }, [main, companions, selected]);

  const bundleTotal = selectedProducts.reduce((sum, p) => sum + (p.price || 0), 0);

  const addAll = useMutation({
    mutationFn: async () => {
      // Every companion added in a single "Add to cart" click shares the
      // same bundle_group UUID so the backend can apply the multi-item
      // discount at checkout time. Generate the id ONCE per click so the
      // whole cart line is grouped even if the user re-clicks later.
      const bundle_group = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `bt-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      // POST each item sequentially so the backend cart is consistent even
      // if one fails. The server side handles idempotency (dupes bump qty).
      for (const p of selectedProducts) {
        await api.post("/marketplace/cart/items", { product_id: p.product_id, quantity: 1, bundle_group });
        ecom.addToCart({
          item_id: p.product_id,
          item_name: p.title,
          item_brand: p.brand || undefined,
          item_category: p.category || undefined,
          price: p.price ?? undefined,
          quantity: 1,
        });
      }
    },
    onSuccess: () => {
      const count = selectedProducts.length;
      const pctLabel = count >= 3 ? "10% off" : count === 2 ? "5% off" : "";
      toast.success(
        `Added ${count} items to your cart${pctLabel ? " · " + pctLabel + " at checkout" : ""}`
      );
      qc.invalidateQueries({ queryKey: ["marketplace-cart"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Please log in to add to cart"),
  });

  if (!companions.length) return null;

  return (
    <section
      className="mb-12 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 sm:p-6"
      data-testid="pdp-buy-together"
      style={{ borderRadius: "0.5em" }}
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-4 w-4 text-[color:var(--sb-saffron)]" />
        <h2 className="font-display text-lg sm:text-xl font-bold tracking-tight text-slate-900 dark:text-white">
          Frequently bought together
        </h2>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-5">
        Bundle these and unlock a smarter checkout. Uncheck anything you don&apos;t need.
      </p>

      <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr] gap-2 sm:gap-3 items-center overflow-x-auto sg-scrollbar-thin pb-2">
        {[main, ...companions].map((p, i, arr) => (
          <React.Fragment key={p.product_id}>
            <BundleTile
              p={p}
              isMain={i === 0}
              checked={!!selected[p.product_id]}
              onToggle={i === 0 ? undefined : () => setSelected((s) => ({ ...s, [p.product_id]: !s[p.product_id] }))}
            />
            {i < arr.length - 1 && (
              <span aria-hidden className="text-slate-400 dark:text-slate-500 flex items-center justify-center">
                <Plus className="h-4 w-4" strokeWidth={2.5} />
              </span>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="mt-5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold">
            Bundle total ({selectedProducts.length} item{selectedProducts.length === 1 ? "" : "s"})
          </div>
          <div
            className="font-display font-bold text-2xl sm:text-3xl text-slate-900 dark:text-white tabular-nums"
            data-testid="pdp-buy-together-total"
          >
            {formatPrice(bundleTotal)}
          </div>
        </div>
        <button
          type="button"
          onClick={() => addAll.mutate()}
          disabled={selectedProducts.length === 0 || addAll.isPending}
          data-testid="pdp-buy-together-add-all"
          className="inline-flex items-center gap-2 bg-[color:var(--sb-saffron)] hover:bg-[color:var(--sb-saffron-hover)] disabled:opacity-50 text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors"
        >
          {addAll.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingBag className="h-4 w-4" />}
          {selectedProducts.length > 1 ? `Add ${selectedProducts.length} to cart` : "Add to cart"}
        </button>
      </div>
    </section>
  );
};

const BundleTile: React.FC<{ p: Product; isMain: boolean; checked: boolean; onToggle?: () => void }> = ({ p, isMain, checked, onToggle }) => {
  const { formatPrice } = useCurrency();
  return (
    <Link
      to={`/shop/p/${p.slug}`}
      onClick={(e) => {
        // If a checkbox toggle handler is provided and the shopper clicks
        // the tile body, prefer the toggle over navigation — but not for
        // the main product (i=0) which stays as a normal link.
        if (onToggle) {
          e.preventDefault();
          onToggle();
        }
      }}
      className={`group block relative rounded-xl border-2 transition-colors bg-white dark:bg-slate-900 min-w-[110px] ${
        checked
          ? "border-[color:var(--sb-saffron)]"
          : "border-slate-200 dark:border-slate-800 opacity-70"
      } ${isMain ? "cursor-default" : "cursor-pointer"}`}
      data-testid={`pdp-buy-together-tile-${p.slug}`}
    >
      {onToggle && (
        <span className="absolute top-1.5 left-1.5 z-10">
          <span
            className={`h-4 w-4 rounded-sm border-2 flex items-center justify-center text-white text-[10px] font-bold ${
              checked
                ? "bg-[color:var(--sb-saffron)] border-[color:var(--sb-saffron)]"
                : "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600"
            }`}
          >
            {checked ? "✓" : ""}
          </span>
        </span>
      )}
      {isMain && (
        <span className="absolute top-1.5 left-1.5 z-10 bg-[color:var(--sb-saffron)] text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">
          This item
        </span>
      )}
      <div className="aspect-square bg-slate-50 dark:bg-slate-800 relative overflow-hidden rounded-t-xl">
        <SmartImage
          src={productImage(p as any, 400)}
          alt={p.title}
          wrapperClassName="absolute inset-0"
          className="h-full w-full object-cover"
        />
      </div>
      <div className="p-2">
        <div className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wider font-semibold truncate">{p.brand || "Unbranded"}</div>
        <div className="text-xs font-semibold text-slate-900 dark:text-white line-clamp-2 min-h-[2.4em] mt-0.5">{p.title}</div>
        <div className="mt-1.5 font-display font-bold text-sm text-slate-900 dark:text-white tabular-nums">
          {formatPrice(p.price || 0)}
        </div>
      </div>
    </Link>
  );
};

export default BuyTogether;
